// resources/js/admin/pages/module-pages/pages/PagesPage.tsx

import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Search, Filter, X, Eye, EyeOff, Edit, Trash2, File } from "lucide-react";
import PageListLayout from "../../../layouts/PageListLayout";
import DeleteConfirmationModal from "../../../components/ui/DeleteConfirmationModal";
import MenuModal, { Menu } from "../../../components/ui/MenuModal";
import PagesList from "../../../components/shared/pages/PagesList";
import { Page } from "../../../types/features/Page";
import { alertSuccess, alertError } from "../../../helpers/alertError";
import { useProduct } from "../../../hooks/core/useProduct";
import usePages from "../../../hooks/features/pages/usePages";

export default function PagesPage() {
    const navigate = useNavigate();
    const { selectedProduct } = useProduct();
    const { pages, loading, deletePage, togglePublish } = usePages(selectedProduct || "izi-call");
    const [searchTerm, setSearchTerm] = useState("");
    const [showFilters, setShowFilters] = useState(false); // ← AJOUTÉ
    const [selectedPage, setSelectedPage] = useState<Page | null>(null);
    const [pageToDelete, setPageToDelete] = useState<Page | null>(null);
    const [itemsToDelete, setItemsToDelete] = useState<string[] | null>(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const [filterStatus, setFilterStatus] = useState<string>("");
    const [filterLocale, setFilterLocale] = useState<string>("");

    // Filtrer les pages
    const filteredPages = useMemo(() => {
        let result = pages;

        if (searchTerm.trim()) {
            const searchLower = searchTerm.toLowerCase();
            result = result.filter(
                (p) =>
                    p.slug_key.toLowerCase().includes(searchLower) ||
                    (p.name && p.name.toLowerCase().includes(searchLower))
            );
        }

        if (filterStatus) {
            result = result.filter((p) => p.status === filterStatus);
        }

     
        return result;
    }, [pages, searchTerm, filterStatus, filterLocale]);

    // Stats
    const stats = {
        total: pages.length,
        published: pages.filter((p) => p.status === "published").length,
        draft: pages.filter((p) => p.status === "draft").length,
        review: pages.filter((p) => p.status === "review").length,
    };

    const handleTogglePublish = async (page: Page) => {
        const success = await togglePublish(page.id);
        if (success) {
            alertSuccess(page.status === "published" ? "Page dépubliée" : "Page publiée");
        } else {
            alertError("Erreur lors du changement de statut");
        }
    };

    const handleDelete = async () => {
        if (!pageToDelete) return;
        setIsDeleting(true);
        const success = await deletePage(pageToDelete.id);
        setIsDeleting(false);
        if (success) {
            setPageToDelete(null);
            alertSuccess("Page supprimée avec succès");
        } else {
            alertError("Erreur lors de la suppression");
        }
    };

    const handleDeleteMany = async () => {
        if (!itemsToDelete || itemsToDelete.length === 0) return;
        let allSuccess = true;
        for (const id of itemsToDelete) {
            const success = await deletePage(id);
            if (!success) allSuccess = false;
        }
        if (allSuccess) {
            setItemsToDelete(null);
            alertSuccess(`${itemsToDelete.length} pages supprimées`);
        } else {
            alertError("Erreur lors de la suppression multiple");
        }
    };

    const clearFilters = () => {
        setSearchTerm("");
        setFilterStatus("");
        setFilterLocale("");
    };

    const hasActiveFilters = searchTerm || filterStatus || filterLocale;

    const getActionsMenu = (page: Page): Menu[] => {
        return [
            {
                label: page.status === "published" ? "Dépublier" : "Publier",
                icon: page.status === "published" ? EyeOff : Eye,
                onClick: () => handleTogglePublish(page),
            },
            {
                label: "Modifier",
                icon: Edit,
                onClick: () => navigate(`/admin/pages/update?id=${page.id}`),
            },
               {
                label: "Editeur de page",
                icon: File,
                onClick: () => navigate(`/admin/pages/editor?id=${page.id}`),
            },
            {
                label: "Supprimer",
                icon: Trash2,
                onClick: () => setPageToDelete(page),
            },
        ];
    };

    return (
        <PageListLayout title="Gestion des pages">
            <div className="px-6 py-4 bg-white border-b border-gray-200">
                <div className="max-w-7xl mx-auto">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                        <div>
                            <h1 className="text-2xl font-bold text-gray-900">Pages</h1>
                            <p className="text-sm text-gray-500 mt-1">
                                Gérez le contenu des pages du site
                            </p>
                        </div>
                        <button
                            onClick={() => navigate("/admin/pages/new")}
                            className="flex items-center justify-center gap-2 px-4 py-2 bg-primary text-white rounded-xl hover:bg-primary/90 transition-colors"
                        >
                            <Plus size={18} />
                            Nouvelle page
                        </button>
                    </div>

                    {/* Stats */}
                    <div className="grid grid-cols-4 gap-4 mt-6">
                        <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-4">
                            <p className="text-xs text-blue-700/70">Total</p>
                            <p className="text-2xl font-bold text-blue-700">{stats.total}</p>
                        </div>
                        <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-4">
                            <p className="text-xs text-green-700/70">Publiées</p>
                            <p className="text-2xl font-bold text-green-700">{stats.published}</p>
                        </div>
                        <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-4">
                            <p className="text-xs text-gray-700/70">Brouillons</p>
                            <p className="text-2xl font-bold text-gray-700">{stats.draft}</p>
                        </div>
                        <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 rounded-xl p-4">
                            <p className="text-xs text-yellow-700/70">En relecture</p>
                            <p className="text-2xl font-bold text-yellow-700">{stats.review}</p>
                        </div>
                    </div>

                    {/* Recherche */}
                    <div className="relative mt-6">
                        <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                        <input
                            type="text"
                            placeholder="Rechercher une page..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary/50 focus:border-primary transition-colors"
                        />
                    </div>

                    {/* Filtres */}
                    <div className="mt-4 flex items-center gap-3 flex-wrap">
                        <button
                            onClick={() => setShowFilters(!showFilters)}
                            className={`flex items-center gap-2 px-4 py-2 rounded-xl transition-all ${
                                showFilters ? "bg-primary text-white" : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                            }`}
                        >
                            <Filter size={18} /> Filtrer
                            {hasActiveFilters && <span className="w-2 h-2 bg-red-500 rounded-full" />}
                        </button>

                        {hasActiveFilters && (
                            <button
                                onClick={clearFilters}
                                className="flex items-center gap-1 px-3 py-2 text-sm text-red-600 hover:text-red-700"
                            >
                                <X size={14} /> Effacer tout
                            </button>
                        )}
                    </div>

                    {showFilters && (
                        <div className="bg-gray-50 rounded-xl p-4 mt-3 space-y-4">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Statut</label>
                                    <select
                                        value={filterStatus}
                                        onChange={(e) => setFilterStatus(e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                    >
                                        <option value="">Tous</option>
                                        <option value="draft">Brouillon</option>
                                        <option value="review">En relecture</option>
                                        <option value="published">Publié</option>
                                        <option value="archived">Archivé</option>
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Langue</label>
                                    <select
                                        value={filterLocale}
                                        onChange={(e) => setFilterLocale(e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                    >
                                        <option value="">Toutes</option>
                                        <option value="fr">Français</option>
                                        <option value="en">English</option>
                                        <option value="es">Español</option>
                                    </select>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            <div className="px-6 py-8 max-w-7xl mx-auto">
                <PagesList
                    pages={filteredPages}
                    onAction={(page) => setSelectedPage(page)}
                    onSelectPages={(selected) => setItemsToDelete(selected.map((p) => p.id))}
                    selectable={true}
                    loading={loading}
                    selectActions={[
                        {
                            label: "Supprimer",
                            onClick: (selected) => setItemsToDelete(selected.map((p) => p.id)),
                            className: "bg-red-600 text-white",
                        },
                    ]}
                />
            </div>

            {/* Menu modal */}
            {selectedPage && (
                <MenuModal
                    title={selectedPage.name || selectedPage.slug_key}
                    isOpen={!!selectedPage}
                    onClose={() => setSelectedPage(null)}
                    icon={null}
                    menu={getActionsMenu(selectedPage)}
                />
            )}

            {/* Modals suppression */}
            <DeleteConfirmationModal
                isOpen={!!pageToDelete}
                onClose={() => setPageToDelete(null)}
                onConfirm={handleDelete}
                title="Supprimer la page"
                message={`Supprimer la page "${pageToDelete?.name || pageToDelete?.slug_key}" ?`}
                confirmText={isDeleting ? "Suppression..." : "Supprimer"}
            />

            <DeleteConfirmationModal
                isOpen={!!itemsToDelete && itemsToDelete.length > 0}
                onClose={() => setItemsToDelete(null)}
                onConfirm={handleDeleteMany}
                title="Suppression multiple"
                message={`Supprimer ${itemsToDelete?.length} page(s) ?`}
            />
        </PageListLayout>
    );
}